/**
 * Homepage hero effects (sections/hero.liquid, marked data-hero-hover).
 * - Neon edge light: four light strips, one along each edge. Each brightens as a light point nears its edge
 *   and is centered on the nearest point of that edge, so a light slides along an edge, passes through a
 *   corner (both edges lit) and carries on down the next edge.
 *   Two light points travel around the hero's border on their own, lap after lap, on opposite sides of the
 *   section. They pause while the hero is off screen or the tab is hidden.
 * Skipped for reduced-motion users.
 */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const EDGE = 0.2; // how close to an edge (fraction of the hero's shorter side) a light starts to show
  const EASE = 0.14;
  const LAP = 11000; // ms for the ambient light to travel once around the hero
  const BRIGHT = 0.9; // brightness of the lights (1 = full)
  const LIGHTS = [0, 0.5]; // where each light starts, as a fraction of a lap (0.5 = the opposite side)
  const EDGES = ['top', 'right', 'bottom', 'left'];

  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

  document.querySelectorAll('[data-hero-hover]').forEach((hero) => {
    const strips = Object.fromEntries(EDGES.map((e) => [e, hero.querySelector(`.hero__neon--${e}`)]));
    const now = {};
    const target = {};
    EDGES.forEach((e) => {
      now[e] = 0;
      target[e] = 0;
      now[`${e}Pos`] = 0;
      target[`${e}Pos`] = 0;
    });
    const placed = {};
    let visible = true;
    let raf = 0;
    let last = 0;
    let lap = 0; // ms into the current ambient lap

    // Light each strip by whichever light point is closest to it.
    function light(points, w, h) {
      const zone = Math.min(w, h) * EDGE;
      EDGES.forEach((e) => {
        let best = 0;
        let pos = target[`${e}Pos`];
        points.forEach(([x, y]) => {
          const dist = { top: y, right: w - x, bottom: h - y, left: x }[e];
          const along = e === 'top' || e === 'bottom' ? x : y;
          const level = Math.pow(clamp(1 - dist / zone, 0, 1), 0.6) * BRIGHT;
          if (level > best) {
            best = level;
            pos = along;
          }
        });
        target[e] = best;
        target[`${e}Pos`] = pos;
        if (!placed[e] && best > 0) {
          now[`${e}Pos`] = pos;
          placed[e] = true;
        }
      });
    }

    // Position on the border after traveling `d` px clockwise from the top-left corner.
    function onBorder(d, w, h) {
      const p = 2 * (w + h);
      d = ((d % p) + p) % p;
      if (d < w) return [d, 0];
      if (d < w + h) return [w, d - w];
      if (d < 2 * w + h) return [w - (d - w - h), h];
      return [0, h - (d - 2 * w - h)];
    }

    function frame(time) {
      raf = 0;
      const dt = last ? Math.min(time - last, 100) : 16;
      last = time;
      const r = hero.getBoundingClientRect();
      const w = r.width;
      const h = r.height;

      lap = (lap + dt) % LAP;
      light(
        LIGHTS.map((offset) => onBorder(((lap / LAP + offset) % 1) * 2 * (w + h), w, h)),
        w,
        h
      );

      for (const key of Object.keys(target)) now[key] += (target[key] - now[key]) * EASE;
      EDGES.forEach((e) => {
        strips[e]?.style.setProperty('--neon', now[e].toFixed(3));
        strips[e]?.style.setProperty('--neon-pos', `${now[`${e}Pos`].toFixed(1)}px`);
        if (now[e] < 0.002 && target[e] === 0) placed[e] = false;
      });

      if (visible) raf = requestAnimationFrame(frame);
    }

    function start() {
      if (!raf && visible && !document.hidden) {
        last = 0;
        raf = requestAnimationFrame(frame);
      }
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          visible = entries.some((entry) => entry.isIntersecting);
          if (visible) start();
        },
        { threshold: 0 }
      ).observe(hero);
    }
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) start();
    });

    start();
  });
})();
